import { FC, useRef, useEffect } from 'react';
import { IonButton } from '@ionic/react';
import { useInstance } from '@meraki-internal/react-dependency-injection';
import { useSubscription } from '@meraki-internal/state';
import { RecordingState } from './RecordingState';
import { Recorder } from './Recorder';
import { HistoryViewModel } from '../../support/HistoryViewModel';
import { CareMapState } from '../../caremap/CareMapState';
import { MixPanelEventEmitter } from '../../metrics/MixPanelEventEmitter';
import { IntercomService } from '../../support/IntercomService';
import { DuoClapperboardPlayIcon, ChevronRightIcon } from '../../theme/Icons';
import { EnvConfiguration } from '../../config/EnvConfiguration';

export interface IRecordingContext {
    type: 'at-appointment' | 'post-appointment' | 'exploring';
    label: string;
    consentHeader: string;
    consentMessage: string;
}

const RECORDING_CONTEXTS: IRecordingContext[] = [
    {
        type: 'at-appointment',
        label: 'At an appointment',
        consentHeader: 'Ready to Capture your Appointment?',
        consentMessage: 'Make sure everyone present gives permission to be recorded.'
    },
    {
        type: 'post-appointment',
        label: 'Just got back from appt',
        consentHeader: 'Capture Notes Now',
        consentMessage: 'Share what you remember—every detail helps create a complete picture.'
    },
    {
        type: 'exploring',
        label: 'Checking this out',
        consentHeader: 'Give Recording a Try!',
        consentMessage: 'Test it out to see how recordings work—you\'ll get a shareable summary and action plan.'
    }
];

interface IRecordingEmptyStateProps {
    onContextSelect?: (context: IRecordingContext) => void;
}

export const RecordingEmptyState: FC<IRecordingEmptyStateProps> = ({ onContextSelect }) => {
    const history = useInstance(HistoryViewModel);
    const careMapState = useInstance(CareMapState);
    const recordingState = useInstance(RecordingState);
    const recordingStateData = useSubscription(() => recordingState);
    const mixpanel = useInstance(MixPanelEventEmitter);
    const intercomService = useInstance(IntercomService);
    const config = useInstance(EnvConfiguration);

    const careMap = careMapState.getMostRecentCaremap();
    const isViewer = careMap && !careMap?.links?.self?.actions.includes('put');

    const addRecipient = () => {
        history.push('/q/new-care-recipient');
    };

    const pendingContextRef = useRef<IRecordingContext | null>(null);

    const handleContextSelect = (context: IRecordingContext) => {
        // Store context in recording state before triggering recording
        recordingState.updateCurrentRecordingExtras({
            initiatedFrom: 'Empty State Card',
            recordingContext: context.type
        });

        // Track analytics
        mixpanel.track('Recording Context Selected', () => ({
            'context': context.type
        }));

        // Store the pending context
        pendingContextRef.current = context;

        // Trigger the recorder click by setting forceStartRecording
        recordingState.setState({ forceStartRecording: true });

        // Call parent callback if provided
        onContextSelect?.(context);
    };

    useEffect(() => {
        if (recordingStateData && recordingState.isRecording() && pendingContextRef.current) {
            pendingContextRef.current = null;
        }
    }, [recordingStateData, recordingState]);

    const handleDemoClick = () => {
        if (intercomService.isEnabled()) {
            intercomService.showArticle(config.RECORDING_DEMO_ARTICLE_ID);
            mixpanel.track('Recording Demo Opened');
        }
    };

    const getIconForContext = (contextType: string) => {
        switch (contextType) {
            case 'at-appointment':
                return <span style={{fontSize: '20px'}}>🏥</span>;
            case 'post-appointment':
                return <span style={{fontSize: '20px'}}>📝</span>;
            case 'exploring':
                return <span style={{fontSize: '20px'}}>👀</span>;
            default:
                return <span style={{fontSize: '20px'}}>👀</span>;
        }
    };

    if (isViewer) {
        return (
            <div style={{display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '20px'}}>
                <div style={{fontSize: 20, fontWeight: 'bold', marginBottom: 8}}>
                    What brings you here today?
                </div>
                <div style={{fontSize: 16, color: '#666', marginBottom: 32}}>
                    Capture what matters with Innerhive
                </div>
                <div style={{textAlign: 'center', marginBottom: 32, maxWidth: 300}}>
                    You do not have permission to add recordings for {careMapState.getPatientFullName()}.&nbsp;
                    <div style={{display: 'inline-block', cursor: 'pointer', color: 'var(--ion-color-primary)'}} onClick={addRecipient}>
                        Add
                    </div> or switch to another care recipient to start recording.
                </div>
            </div>
        );
    }

    return (
        <div style={{display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '20px'}}>
            <div style={{fontSize: 20, fontWeight: 'bold', marginBottom: 8}}>
                What brings you here today?
            </div>
            <div style={{fontSize: 16, color: '#666', marginBottom: 24}}>
                Capture what matters with Innerhive
            </div>

            {/* Contextual Buttons */}
            <div style={{display: 'flex', flexDirection: 'column', gap: '12px', width: '100%', maxWidth: '400px', marginBottom: 24}}>
                {RECORDING_CONTEXTS.map((context) => (
                    <IonButton
                        key={context.type}
                        expand="block"
                        fill="outline"
                        color="primary"
                        style={{
                            height: '48px',
                            fontSize: '16px',
                            fontWeight: '500',
                            '--padding-start': '16px',
                            '--padding-end': '16px'
                        }}
                        data-id={`recording-context-${context.type}`}
                        onClick={() => handleContextSelect(context)}
                    >
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            width: '100%'
                        }}>
                            <div style={{display: 'flex', alignItems: 'center', gap: '12px'}}>
                                <div style={{width: '20px', display: 'flex', justifyContent: 'center'}}>
                                    {getIconForContext(context.type)}
                                </div>
                                <span>{context.label}</span>
                            </div>
                            <ChevronRightIcon />
                        </div>
                    </IonButton>
                ))}
            </div>

            {/* Demo Link */}
            {intercomService.isEnabled() && (
                <div style={{textAlign: 'center'}}>
                    <span
                        style={{
                            color: 'var(--ion-color-primary)',
                            cursor: 'pointer',
                            fontSize: '14px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '8px'
                        }}
                        onClick={handleDemoClick}
                        data-id="recording-demo-link"
                    >
                        <DuoClapperboardPlayIcon />
                        Learn more with this quick video
                    </span>
                </div>
            )}

            {/* Hidden Recorder that handles the actual recording logic */}
            <div style={{display: 'none'}}>
                <Recorder trackingFrom="Record Widget" cannotRecordClickHandler={addRecipient} />
            </div>
        </div>
    );
};
